import React, { useMemo, useState, useEffect } from "react";
import { Line } from "react-chartjs-2";
import { useSelector } from "react-redux";
import { FormatIndianNumber } from "../utiles/FormatIndianNumber";
import {formatNumberShort,formatNumberShortForGraph} from "../utiles/langNumber"

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import useLocale from "../../src/locales/index";
import { useTranslation } from "react-i18next";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const ContributionChart = () => {
  const { textData } = useLocale();
  const { i18n } = useTranslation();
  const langCode = i18n.language || "default";

  const { chartData: calculatorChartData } = useSelector(
    (state) => state.calculator
  );
  const contributionData = calculatorChartData["contributionCalc"] || [];
  
  // Get input values from Redux (for calculations and empty state display)
  const {
    currentAge,
    retirementAge,
    returnRate,
    currentBalance,
    employerMatch,
    salaryLimit,
    monthlyContribution,
    annualSalary,
  } = useSelector((state) => state.contributionCalculator);

  const { isContributionChartCalc } = useSelector((state) => state.calculator);

  const [hasValidCalculation, setHasValidCalculation] = useState(false);
  // Store the calculated data when calculation is triggered
  const [storedCalculatedData, setStoredCalculatedData] = useState([]);
  // Store the calculation timestamp to force recalculation
  const [calculationTimestamp, setCalculationTimestamp] = useState(0);

  // Check if we have valid calculation data - only based on calculated results
  useEffect(() => {
    const hasCalculatedData = 
      isContributionChartCalc && 
      contributionData.length > 0 &&
      contributionData.some(item => item.value > 0);

    setHasValidCalculation(hasCalculatedData);
    
    // Update timestamp when calculation state changes
    if (hasCalculatedData) {
      setCalculationTimestamp(Date.now());
    }
  }, [isContributionChartCalc, contributionData]);

  // Only calculate when calculation is triggered (button clicked)
  useEffect(() => {
    if (!hasValidCalculation || calculationTimestamp === 0) {
      setStoredCalculatedData([]);
      return;
    }

    const data = [];
    const yearsToRetirement = retirementAge - currentAge;
    const monthlyReturn = returnRate / 100 / 12;
    const monthlyContributionRate = monthlyContribution / 100;
    const employerMatchRate = employerMatch / 100;
    const salaryLimitRate = salaryLimit / 100;

    let currentSalary = annualSalary;
    let balanceWithMatch = currentBalance;
    let balanceWithoutMatch = currentBalance;

    data.push({
      age: currentAge,
      balanceWithMatch,
      balanceWithoutMatch,
    });

    for (let year = 1; year <= yearsToRetirement; year++) {
      const age = currentAge + year;

      const monthlyEmployeeContribution =
        (currentSalary * monthlyContributionRate) / 12;
      const maxEmployerMatch = (currentSalary * salaryLimitRate) / 12;
      const actualEmployerMatch =
        Math.min(maxEmployerMatch, monthlyEmployeeContribution) *
        employerMatchRate;
      const monthlyTotalContribution =
        monthlyEmployeeContribution + actualEmployerMatch;

      for (let month = 0; month < 12; month++) {
        balanceWithMatch =
          (balanceWithMatch + monthlyTotalContribution) * (1 + monthlyReturn);
        balanceWithoutMatch =
          (balanceWithoutMatch + monthlyEmployeeContribution) *
          (1 + monthlyReturn);
      }

      data.push({
        age,
        balanceWithMatch,
        balanceWithoutMatch,
      });
    }

    setStoredCalculatedData(data);
  }, [calculationTimestamp]); // Only depends on calculationTimestamp

  const calculatedData = useMemo(() => {
    return hasValidCalculation ? storedCalculatedData : [];
  }, [hasValidCalculation, storedCalculatedData]);

  const ages = useMemo(() => {
    return calculatedData.map((item) => item.age);
  }, [calculatedData]);

  const balancesWithMatch = useMemo(() => {
    return calculatedData.map((item) => item.balanceWithMatch);
  }, [calculatedData]);

  const balancesWithoutMatch = useMemo(() => {
    return calculatedData.map((item) => item.balanceWithoutMatch);
  }, [calculatedData]);

  const data = {
    labels: ages,
    datasets: [
      {
        label: textData.chartLabels.withEmploye,
        data: balancesWithMatch,
        backgroundColor: "rgba(87, 198, 244, 0.2)",
        borderColor: "#57C6F4",
        pointBackgroundColor: "#57C6F4",
        pointBorderColor: "#ffffff",
        pointHoverBackgroundColor: "#ffffff",
        pointHoverBorderColor: "#57C6F4",
        pointRadius: 4,
        pointHoverRadius: 6,
        tension: 0.3,
        fill: false,
        borderWidth: 1,
      },
      {
        label: textData.chartLabels.withoutEmploye,
        data: balancesWithoutMatch,
        backgroundColor: "rgba(255, 133, 102, 0.2)",
        borderColor: "#FF8566",
        pointBackgroundColor: "#FF8566",
        pointBorderColor: "#ffffff",
        pointHoverBackgroundColor: "#ffffff",
        pointHoverBorderColor: "#FF8566",
        pointRadius: 4,
        pointHoverRadius: 6,
        tension: 0.3,
        fill: false,
        borderWidth: 1,
      },
    ],
  };

  // Empty state with placeholder background
  const emptyData = {
    labels: [currentAge || 25, retirementAge || 60],
    datasets: [
      {
        label: textData.chartLabels.withEmploye,
        data: [0, 0],
        backgroundColor: "rgba(87, 198, 244, 0.1)",
        borderColor: "rgba(87, 198, 244, 0.3)",
        borderWidth: 1,
        borderDash: [5, 5],
      },
      {
        label: textData.chartLabels.withoutEmploye,
        data: [0, 0],
        backgroundColor: "rgba(255, 133, 102, 0.1)",
        borderColor: "rgba(255, 133, 102, 0.3)",
        borderWidth: 1,
        borderDash: [5, 5],
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
        layout: {
      padding: {
        left: 0,
        right: 20,
        top: 20,
        bottom: 20
      }
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        // Disable tooltip for empty state
        enabled: hasValidCalculation,
        backgroundColor: "rgba(255, 255, 255, 0.9)",
        titleColor: "#333",
        bodyColor: "#333",
        borderColor: "#ddd",
        borderWidth: 1,
        padding: 10,
        cornerRadius: 4,
        displayColors: true,
        titleFont: {
          size: 12,
          weight: "bold",
        },
        bodyFont: {
          size: 11,
          weight: "normal",
        },
        callbacks: {
          title: function (context) {
            return `${textData.age}: ${context[0].label}`;
          },
          label: function (context) {
            let label = context.dataset.label || "";
            const value = `${textData.currency} ${FormatIndianNumber(
              context.parsed.y
            )}`;

            if (label.length > 25) {
              label = label.substring(0, 22) + "...";
            }

            return `${label}: ${value}`;
          },
          labelTextColor: function (context) {
            return context.dataset.borderColor;
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
          drawOnChartArea: false,
        },
        border: {
          dash: [4, 4],
          color: "rgba(0, 0, 0, 0.3)",
          width: 1,
        },
        title: {
          display: false,
          text: "Age",
        },
        ticks: {
          // Add padding to prevent label cutoff
          padding: 10,
          maxRotation: 0,
          autoSkip: true,
          maxTicksLimit: 10,
          align: "end", // Ensures text starts inside chart
          crossAlign: "near",
        }
      },
      y: {
        grid: {
          display: true,
          drawBorder: true,
          color: "rgba(200, 200, 200, 0.3)",
          lineWidth: 1,
          drawTicks: false,
          drawOnChartArea: true,
        },
        border: {
          dash: [4, 4],
          width: 0,
        },
        ticks: {
          align: "end",           // <--- Ensures label is aligned inside
          crossAlign: "near",
          callback: function (value) {
            if (!hasValidCalculation) return value;

            return formatNumberShortForGraph(value, i18n.language);
          },
          font: {
            size: 10,
            family: "Arial",
            weight: "normal",
          },
          padding: 15,
          maxTicksLimit: 8
        },
        beginAtZero: true,
        grace: '10%', // Adds buffer to the top
        suggestedMin: 0,
      },
    },
  };

  const withEmployerValue = contributionData[0]?.value || 0;
  const withoutEmployerValue = contributionData[1]?.value || 0;

  return (
    <div className="flex flex-col w-full">
      {/* Chart Container */}
      <div className="bg-white pt-6 px-4 pb-4 mb-2">
        <div className="h-[325px] relative">
          {hasValidCalculation ? (
            <Line data={data} options={options} />
          ) : (
            <div className="h-full flex flex-col items-center justify-center">
              <Line data={emptyData} options={options} />
              <div className="absolute inset-0  items-center justify-center pointer-events-none">
                <div className="text-gray-400 text-center">
              </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-col">
        <div className="flex items-center pt-4 sm:pt-4 pb-4 justify-between border-b border-solid border-gray-light">
          <div className="flex items-center">
            <div className="h-5 w-5 bg-[#57C6F4] rounded-[4px] mr-2"></div>
            <span className="text-sm sm:text-sm text-black font-normal text-start">
              {" "}
              {textData.chartLabels.withEmploye}
            </span>
          </div>
          <span className="text-sm sm:text-sm text-black font-medium whitespace-nowrap">
          {hasValidCalculation
    ? `${textData.currency}${formatNumberShort(withEmployerValue,
      langCode
    )}`
    : "--"}
          </span>
        </div>

        <div className="flex items-center justify-between pt-4 sm:pt-4 pb-0">
          <div className="flex items-center">
            <div className="h-5 w-5 bg-[#FF8566] rounded-[4px] mr-2"></div>
            <span className="text-sm sm:text-sm text-black font-normal text-start">
              {textData.chartLabels.withoutEmploye}
            </span>
          </div>
          <span className="text-sm sm:text-sm text-black font-medium whitespace-nowrap">
          {hasValidCalculation
  ? `${textData.currency}${formatNumberShort(withoutEmployerValue,langCode)}`
  : "--"}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ContributionChart;